<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <h1 class="title">Invoices</h1>
      <app-cells>
        <app-input
          @input.native="onInvoiceSearch"
          v-model="filter_search"
          class="input--300"
          type="search"
          placeholder="Search"
          name="search"
        />
        <app-select
          @input="onChangeFilter"
          v-model="filter_manager"
          :options="filter_manager_options"
          :reduce="item => item.id"
          label="full_name"
          placeholder="Manager"
          class="select--300"
        />
      </app-cells>
      <app-cells>
        <h2 class="title">Sent</h2>
        <VueCtkDateTimePicker
          id="date_picker_sent"
          class="custom-date-time-picker custom-date-time-picker--range"
          v-model="filter_dates_sent"
          :custom-shortcuts="custom_shortcuts"
          @is-hidden="onIsHiddenPickerSent"
          @input="onInputDatePickerSent"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Date range"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          range
          ref="date_picker"
        />
        <app-filters
          v-model="filter_charge"
          :filters="filter_charge_options"
          name="filterCharge"
          @change="onChangeFilterSent"
        />
      </app-cells>
      <v-client-table
        :data="table_sent.items"
        :columns="table_sent.columns"
        :options="table_sent.options"
        class="table-default"
        :class="{ 'table-default--loading': table_sent_preloader }"
      >
        <template #h__invoice><div class="table-default__left">Invoice</div></template>
        <template #h__payer><div class="table-default__left">Payer</div></template>
        <template #h__account><div class="table-default__left">Account</div></template>
        <template #h__method><div class="table-default__left">Method</div></template>
        <template #h__lead><div class="table-default__left">Lead</div></template>
        <template #h__status><div class="table-default__left">Status</div></template>
        <template #invoice="{ row }">
          <a :href="`https://story.altezza.travel/changeInvoice/${row.story_id}`" target="_blank" class="table-default__link-border">
            {{ row.number }}
          </a>
        </template>
        <template #deadline="{ row }">
          <span class="status-tag" style="background-color: #e85c20" v-if="isDatePast(row.deadline)">{{ dateFormat(row.deadline) }}</span>
          <template v-else>{{ dateFormat(row.deadline) }}</template>
        </template>
        <template #payer="{ row }"><div class="table-default__left">{{ row.payer }}</div></template>
        <template #amount_payable_usd="{ row }">{{ amountFormat(row.amount_payable_usd) }} $</template>
        <template #method="{ row }"><div class="table-default__left">{{ row.charge_type?.name || '-' }}</div></template>
        <template #account="{ row }"><div class="table-default__left">{{ row.account.name }}</div></template>
        <template #lead="{ row }">
          <div class="table-default__controls table-default__left">
            <template v-if="row.itinerary?.lead">
              <div class="table-default__photo">
                <img
                  :src="getUserAvatar(row.itinerary.lead.manager.crop.crop_url)" alt="Avatar"
                  v-tooltip="{ content: `${row.itinerary.lead.manager.full_name || ''}`, placement: 'top-start', offset: 10 }"
                >
              </div>
              <router-link
                v-if="row.itinerary.lead.client_group"
                :to="{ name: 'clients-groups-payment-control', params: { id: row.itinerary.lead.client_group.id } }"
                class="table-default__link-border"
              >
                {{ row.itinerary.lead.name }}
              </router-link>
              <router-link
                v-else
                :to="{ name: 'leads-detail-lead', params: { id: row.itinerary.lead.id } }"
                class="table-default__link-border"
              >
                {{ row.itinerary.lead.name }}
              </router-link>
            </template>
            <template v-else>-</template>
          </div>
        </template>
        <template #status="{ row }">
          <div class="table-default__left">
            <span v-if="row.itinerary?.lead" class="status-tag" :style="{ backgroundColor: row.itinerary.lead.status.color }">
              {{ row.itinerary.lead.status.name }}
            </span>
            <template v-else>-</template>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPaginationSent"
            v-if="pagination_sent.pages > 1"
            v-model="pagination_sent.page"
            :page-count="pagination_sent.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_sent.count" :count="table_sent.items.length" :page="pagination_sent.page" :limit="10" />
      </app-cells>
      <app-cells>
        <h2 class="title">Paid</h2>
        <VueCtkDateTimePicker
          id="date_picker_paid"
          class="custom-date-time-picker custom-date-time-picker--range"
          v-model="filter_dates_paid"
          :custom-shortcuts="custom_shortcuts"
          @is-hidden="onIsHiddenPickerPaid"
          @input="onInputDatePickerPaid"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Date range"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          range
          ref="date_picker_paid"
        />
        <app-select
          @input="onChangeFilterPaid"
          v-model="filter_method"
          :reduce="item => item.code"
          :options="filter_method_options"
          placeholder="Payment method"
          class="select--300"
        />
      </app-cells>
      <v-client-table
        :data="table_paid.items"
        :columns="table_paid.columns"
        :options="table_paid.options"
        class="table-default"
        :class="{ 'table-default--loading': table_paid_preloader }"
      >
        <template #h__invoice><div class="table-default__left">Invoice</div></template>
        <template #h__payer><div class="table-default__left">Payer</div></template>
        <template #h__payment_method><div class="table-default__left">Method</div></template>
        <template #h__lead><div class="table-default__left">Lead</div></template>
        <template #invoice="{ row }">
          <a :href="`https://story.altezza.travel/changeInvoice/${row.story_id}`" target="_blank" class="table-default__link-border">
            {{ row.number }}
          </a>
        </template>
        <template #date_of_payment="{ row }">{{ dateFormat(row.date_of_payment) }}</template>
        <template #payer="{ row }"><div class="table-default__left">{{ row.payer }}</div></template>
        <template #amount_payable_usd="{ row }">{{ amountFormat(row.amount_payable_usd) }} $</template>
        <template #payment_method="{ row }"><div class="table-default__left">{{ row.payment_method?.name || '-' }}</div></template>
        <template #lead="{ row }">
          <div class="table-default__controls table-default__left">
            <template v-if="row.itinerary?.lead">
              <div class="table-default__photo">
                <img
                  :src="getUserAvatar(row.itinerary.lead.manager.crop.crop_url)" alt="Avatar"
                  v-tooltip="{ content: `${row.itinerary.lead.manager.full_name || ''}`, placement: 'top-start', offset: 10 }"
                >
              </div>
              <router-link
                v-if="row.itinerary.lead.client_group"
                :to="{ name: 'clients-groups-payment-control', params: { id: row.itinerary.lead.client_group.id } }"
                class="table-default__link-border"
              >
                {{ row.itinerary.lead.name }}
              </router-link>
              <router-link
                v-else
                :to="{ name: 'leads-detail-lead', params: { id: row.itinerary.lead.id } }"
                class="table-default__link-border"
              >
                {{ row.itinerary.lead.name }}
              </router-link>
            </template>
            <template v-else>-</template>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPaginationPaid"
            v-if="pagination_paid.pages > 1"
            v-model="pagination_paid.page"
            :page-count="pagination_paid.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_paid.count" :count="table_paid.items.length" :page="pagination_paid.page" :limit="10" />
      </app-cells>
    </template>
  </div>
</template>

<script>
import { getFinanceInvoiceList, getFinanceInvoicePaymentMethods, getUsers } from '@/http'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import debounce from 'lodash/debounce'
import { format, isPast, parse } from 'date-fns'

export default {
  name: 'ClientsInvoices',
  components: { VueCtkDateTimePicker },
  data() {
    return {
      page_loaded: false,
      filter_search: '',
      filter_manager: null,
      filter_manager_options: [],
      filter_method: null,
      filter_method_options: [],
      filter_charge: '',
      filter_charge_options: [
        { value: '', label: 'All' },
        { value: 'standard', label: 'Standard' },
        { value: 'spot', label: 'Spot' },
        { value: 'auth_form', label: 'Auth form' },
      ],
      filter_dates_sent: null,
      filter_dates_paid: null,
      custom_shortcuts: [
        { key: 'last7Days', label: 'Last 7 days', value: 7 },
        { key: 'last14Days', label: 'Last 14 days', value: 14 },
      ],
      table_sent: {
        items: [],
        columns: ['invoice', 'deadline', 'payer', 'amount_payable_usd', 'method', 'account', 'lead', 'status'],
        options: {
          headings: {
            invoice: 'Invoice',
            deadline: 'Deadline',
            payer: 'Payer',
            amount_payable_usd: 'Payable',
            payment_method: 'Method',
            account: 'Account',
            lead: 'Lead',
            status: 'Status',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_sent: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_sent_preloader: false,
      table_paid: {
        items: [],
        columns: ['invoice', 'date_of_payment', 'payer', 'amount_payable_usd', 'payment_method', 'lead'],
        options: {
          headings: {
            invoice: 'Invoice',
            date_of_payment: 'Date',
            payer: 'Payer',
            amount_payable_usd: 'Amount',
            payment_method: 'Method',
            lead: 'Lead',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_paid: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_paid_preloader: false,
    }
  },
  created() {
    Promise.all([
      getUsers({ group: 'sales_manager', is_active: true }),
      getFinanceInvoicePaymentMethods(),
      this.fetchItemsSent(),
      this.fetchItemsPaid()
    ])
      .then(response => {
        this.filter_manager_options = response[0].data
        this.filter_method_options = response[1].data
        this.page_loaded = true
      })
  },
  methods: {
    async fetchItemsSent() {
      this.table_sent_preloader = true
      const response = await getFinanceInvoiceList({
        page: this.pagination_sent.page,
        page_size: 20,
        status: 'sent',
        ordering: 'deadline',
        search: this.filter_search,
        manager: this.filter_search ? '' : this.filter_manager,
        deadline_from: this.filter_search ? '' : this.filter_dates_sent?.start,
        deadline_to: this.filter_search ? '' : this.filter_dates_sent?.end,
        charge_type: this.filter_search ? '' : this.filter_charge,
      })
      this.table_sent.items = response.data.results
      this.pagination_sent.pages = response.data.pages
      this.pagination_sent.count = response.data.count
      this.table_sent_preloader = false
    },
    async fetchItemsPaid() {
      this.table_paid_preloader = true
      const response = await getFinanceInvoiceList({
        page: this.pagination_paid.page,
        page_size: 20,
        status: 'paid,partially',
        ordering: '-date_of_payment',
        search: this.filter_search,
        manager: this.filter_search ? '' : this.filter_manager,
        date_of_payment_from: this.filter_search ? '' : this.filter_dates_paid?.start,
        date_of_payment_to: this.filter_search ? '' : this.filter_dates_paid?.end,
        payment_method: this.filter_search ? '' : this.filter_method,
      })
      this.table_paid.items = response.data.results
      this.pagination_paid.pages = response.data.pages
      this.pagination_paid.count = response.data.count
      this.table_paid_preloader = false
    },
    onClickPaginationSent(page) {
      this.pagination_sent.page = page
      this.fetchItemsSent()
    },
    onClickPaginationPaid(page) {
      this.pagination_paid.page = page
      this.fetchItemsPaid()
    },
    fetchAllTables() {
      this.fetchItemsSent()
      this.fetchItemsPaid()
    },
    onChangeFilter() {
      this.filter_search = ''
      this.pagination_sent.page = 1
      this.pagination_paid.page = 1
      this.fetchAllTables()
    },
    onChangeFilterSent() {
      this.pagination_sent.page = 1
      this.fetchItemsSent()
    },
    onInputDatePickerSent() {
      if (!this.$refs.date_picker.pickerOpen) {
        this.pagination_sent.page = 1
        this.fetchItemsSent()
      }
    },
    onIsHiddenPickerSent() {
      if (this.filter_dates_sent?.start) {
        this.pagination_sent.page = 1
        this.fetchItemsSent()
      }
    },
    onChangeFilterPaid() {
      this.pagination_paid.page = 1
      this.fetchItemsPaid()
    },
    onInputDatePickerPaid() {
      if (!this.$refs.date_picker_paid.pickerOpen) {
        this.pagination_paid.page = 1
        this.fetchItemsPaid()
      }
    },
    onIsHiddenPickerPaid() {
      if (this.filter_dates_paid?.start) {
        this.pagination_paid.page = 1
        this.fetchItemsPaid()
      }
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    isDatePast(date) {
      return isPast(parse(date, 'yyyy-MM-dd', new Date()))
    },
    amountFormat(amount) {
      return new Intl.NumberFormat('ru-RU').format(amount)
    },
    onInvoiceSearch() {
      this.searchInvoice(this)
    },
    searchInvoice: debounce(vm => {
      vm.pagination_sent.page = 1
      vm.pagination_paid.page = 1
      vm.fetchAllTables()
    }, 350),
  }
}
</script>
